import {
  Component,
  ComponentRef,
  ElementRef,
  Renderer2,
  ViewChild,
  ViewContainerRef,
  computed,
  effect,
  inject,
  input,
  signal,
  Output,
  EventEmitter,
  OnInit,
} from '@angular/core'; // Added signal imports, Output, EventEmitter
import { UiStateService } from '@dataclouder/ngx-core';
import { FormGroup, FormControl, Validators } from '@angular/forms'; // Use typed forms
import { KeyValuePipe } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { LessonAIService } from '../../../services/lesson-ai.service'; // Import the new service

import { ILesson, ILessonTaken, LessonComponentConfiguration, LESSONS_TOKEN } from '../../lesson-mini-components/components/lessons.clases';

import { EnglishEvaluationSkill } from './eval-agents-skills';

import { EModelQuality, TOAST_ALERTS_TOKEN, ToastAlertsAbstractService } from '@dataclouder/ngx-core';
import { LessonComponentInterface } from '../../lesson-mini-components/components/lessons.clases';
import {
  DCChatComponent,
  IAgentCard,
  IConversationSettings,
  EvalResultStringDefinition,
  UserDataExchangeAbstractService,
  USER_DATA_EXCHANGE,
  SimpleAgentTask,
  ChatEvent,
  ChatEventType,
  WordData,
  ConversationEvents,
  IConversationFlow,
  ConditionType,
  ConditionOperator,
  EDoActionType,
  SystemPromptType,
} from '@dataclouder/ngx-agent-cards';

import { DrawerModule } from 'primeng/drawer';
import { DynamicComponentsService } from '../../dynamic-components/dynamic-components.service';
import { DefaultLessonsService } from '@dataclouder/ngx-lessons';

export interface ILessonsSettings {
  additionalPrompt: string;
}

@Component({
  selector: 'dc-lesson-renderer',
  templateUrl: './dc-lesson-renderer.component.html',
  styleUrls: ['./dc-lesson-renderer.component.css'],
  standalone: true,
  imports: [KeyValuePipe, ButtonModule, DCChatComponent, DrawerModule],
})
export class DCLessonRendererComponent implements OnInit {
  // --- Signal Inputs ---
  lessonInput = input<ILesson>(); // Input signal for lesson object
  lessonIdInput = input<string>(); // Input signal for lesson ID
  settings = input<ILessonsSettings>();

  // --- Outputs ---
  @Output() wordClicked = new EventEmitter<WordData>(); // New output event

  // --- View Childs ---
  @ViewChild('dynamicLesson', { static: true }) dynamicLesson: ElementRef<HTMLElement>;

  // --- Services  ---
  private readonly renderer = inject(Renderer2);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly toastrService = inject<ToastAlertsAbstractService>(TOAST_ALERTS_TOKEN);
  private lessonsService = inject(LESSONS_TOKEN, { optional: true }) ?? inject(DefaultLessonsService);
  private readonly lessonAIService = inject(LessonAIService); // Inject the new service
  private readonly userDataExchange = inject<UserDataExchangeAbstractService>(USER_DATA_EXCHANGE);
  private readonly dynamicComponentsService = inject(DynamicComponentsService);
  public readonly uiStateService = inject<UiStateService>(UiStateService);

  // --- State Signals ---
  lesson = signal<ILesson | undefined>(undefined); // Internal lesson state signal
  // chatVisible = this.uiStateService.chatDrawerVisible; // Signal for chat visibility
  agentMasterLesson = signal<IAgentCard | undefined>(undefined); // Signal for agent card
  evaluatorAgentCard = signal<SimpleAgentTask | undefined>(undefined); // Signal for evaluator card
  conversationSettings = signal<IConversationSettings | undefined>(undefined);

  evalAgentTask = signal<SimpleAgentTask | undefined>(undefined); // Signal for evaluator card
  // backgroundTasks: Record<ConversationEvents | string, SimpleAgentTask> = {};
  conversationFlow = signal<IConversationFlow | undefined>(undefined);

  // --- Computed Signals ---
  imageCover = computed(() => this.lesson()?.media?.images?.find((img) => img.type === 'cover')?.url); // Computed signal for imageCover

  // --- Properties ---
  private components: { [key: string]: ComponentRef<LessonComponentInterface> } = {};
  public mainForm: FormGroup<{ [key: string]: FormControl<any> }> = new FormGroup({});
  private previousTextCoded: string | undefined | null = undefined; // Store previous value

  constructor() {
    // Effect to fetch lesson data if ID is provided and lesson object isn't
    effect(
      async () => {
        const lessonInput = this.lessonInput();
        const lessonId = this.lessonIdInput();

        if (lessonInput) {
          this.lesson.set(lessonInput); // Use input lesson directly
        } else if (lessonId && !this.lesson()) {
          // Fetch only if ID exists and internal lesson is not set
          console.log(`[Renderer] Effect 1: Fetching lesson ${lessonId}`);
          try {
            // Consider adding a loading state signal here
            const fetchedLesson = await this.lessonsService.getLesson(lessonId);
            this.lesson.set(fetchedLesson);
            console.log('Fetched lesson:', fetchedLesson);
          } catch (error) {
            console.error(`Failed to fetch lesson with ID: ${lessonId}`, error);
            this.toastrService.error({ subtitle: 'Failed to load lesson data.', title: 'Error' });
            this.lesson.set(undefined); // Reset lesson on error
          } finally {
            // Reset loading state signal here
          }
        } else if (!lessonInput && !lessonId) {
          // Handle case where neither input is provided, maybe clear the lesson?
          this.lesson.set(undefined);
        }
      },
      { allowSignalWrites: true },
    ); // Allow signal writes inside effect

    // Effect to render the lesson only when textCoded value actually changes
    effect(() => {
      const currentLesson = this.lesson(); // Read the lesson signal
      const newTextCoded = currentLesson?.textCoded; // Get the current textCoded value

      this.evalAgentTask.set({
        systemPrompt: EnglishEvaluationSkill,
        model: { provider: 'google' },
        task: `Please evaluate the user conversation student data is: \n ${this.userDataExchange.getUserDataInformation()}`,
        expectedResponseType: EvalResultStringDefinition,
      });

      // Quick fix to only render on changes textCode not all the lesson
      if (newTextCoded !== this.previousTextCoded) {
        if (newTextCoded !== undefined && newTextCoded !== null && currentLesson) {
          this._renderLesson(currentLesson);
        } else {
          this._clearLessonRendering();
        }
        // Update the stored previous value *after* comparison and action
        this.previousTextCoded = newTextCoded;
      } else {
        console.log('[Renderer] textCoded has NOT changed. Skipping render/clear.');
      }
    });
  }
  ngOnInit(): void {
    // TODO: esto es mal, el goal no puede venir aqui sino pasado desde el componente que inicie la lección
    this.conversationFlow.set({
      goal: {
        enabled: true,
        task: `User is learning is taking a lesson about languages, evaluate how good are the responses.`,
        model: { quality: EModelQuality.FAST },
      },
      triggerTasks: {
        [ConversationEvents.OnUserMessage]: {
          enabled: true,
          ...this.evalAgentTask(),
        },
      },
      challenges: null,
      dynamicConditions: [
        {
          what: ConditionType.Goal,
          when: ConditionOperator.GreaterThanOrEqual,
          value: 100,
          do: [
            {
              actionType: EDoActionType.ChangePrompt,
              systemPromptType: SystemPromptType.SystemPrompt,
              prompt:
                'You were talking with the user, user finish the conversation, in the next message you must try to finish the conversation and say good bye.',
            },
            {
              actionType: EDoActionType.ChangePrompt,
              systemPromptType: SystemPromptType.CharacterDescription,
              prompt: '',
            },
            {
              actionType: EDoActionType.ChangePrompt,
              systemPromptType: SystemPromptType.UserInformation,
              prompt: '',
            },
            {
              actionType: EDoActionType.ChangePrompt,
              systemPromptType: SystemPromptType.MessageExamples,
              prompt: '',
            },
            {
              actionType: EDoActionType.ChangePrompt,
              systemPromptType: SystemPromptType.ScenarioDescription,
              prompt: '',
            },
          ],
        },
      ],
    });
  }

  // --- Rendering Logic ---
  private _clearLessonRendering(): void {
    // Destroy previously created dynamic components
    Object.values(this.components).forEach((compRef) => compRef.destroy());
    this.components = {};
    // Clear the form
    this.mainForm = new FormGroup({});
    // Clear the HTML content
    if (this.dynamicLesson?.nativeElement) {
      this.dynamicLesson.nativeElement.innerHTML = '';
    }
  }

  private _renderLesson(lessonData: ILesson): void {
    this._clearLessonRendering(); // Clear previous state first
    console.log('Rendering lesson:', lessonData.id);
    // 1) Parse textCoded, create components, and build HTML structure
    const { htmlContent, components } = this._parseAndCreateComponents(lessonData);
    this.components = components;
    // 2) Aggregate form controls from created components
    this._aggregateFormControls(this.components);
    // 3) Set the innerHTML of the target element
    this.dynamicLesson.nativeElement.innerHTML = htmlContent;
    // 4) Inject the component views into the DOM
    this._injectComponentsIntoDom(this.components);
  }

  private _parseAndCreateComponents(lessonData: ILesson): { htmlContent: string; components: { [key: string]: ComponentRef<LessonComponentInterface> } } {
    const r1 = new RegExp('~(.+?)~', 'g');
    let count = 0;
    const createdComponents: { [key: string]: ComponentRef<LessonComponentInterface> } = {};

    const htmlContent = lessonData.textCoded.replace(r1, (_matching, jsonCoded) => {
      const componentName = `dynamicComp${count}`;
      count++;
      const componentRef = this._createComponentReferenceWithJson(jsonCoded, lessonData);
      if (!componentRef) {
        console.error(`Failed to create component for: ${jsonCoded}`);
        return '<!-- component creation failed -->'; // Placeholder in HTML
      }
      createdComponents[componentName] = componentRef;
      return `<span id="${componentName}"></span>`; // Return span placeholder
    });

    return { htmlContent, components: createdComponents };
  }

  private _aggregateFormControls(components: { [key: string]: ComponentRef<LessonComponentInterface> }): void {
    const newFormControls: { [key: string]: FormControl<any> } = {};
    Object.entries(components).forEach(([name, componentRef]) => {
      // Check if the instance has a control property that is a FormControl
      if (componentRef.instance?.control instanceof FormControl) {
        newFormControls[name] = componentRef.instance.control;
        // Add required validator (consider making this configurable via component config?)
        newFormControls[name].addValidators(Validators.required);
      }
    });
    this.mainForm = new FormGroup(newFormControls); // Create the typed FormGroup
  }

  private _injectComponentsIntoDom(components: { [key: string]: ComponentRef<LessonComponentInterface> }): void {
    Object.entries(components).forEach(([name, componentRef]) => {
      const elementRef = document.getElementById(name); // Find the placeholder span
      if (elementRef) {
        this._addComponentToNode(componentRef, elementRef);
      } else {
        console.warn(`Placeholder element with ID '${name}' not found in the DOM.`);
      }
    });
  }

  private _addComponentToNode(componentRef: ComponentRef<LessonComponentInterface>, nodeDOM: HTMLElement): void {
    this.renderer.appendChild(nodeDOM, componentRef.location.nativeElement);
  }

  private _createComponentReferenceWithJson(json: string, currentLesson: ILesson): ComponentRef<LessonComponentInterface> | null {
    try {
      let lessonCodedConfig: LessonComponentConfiguration = JSON.parse(json);
      //
      // Attempt to find pre-configured component data in the lesson object by ID
      if (lessonCodedConfig.id && currentLesson?.dynamicComponents[lessonCodedConfig.id]) {
        const foundConfig = currentLesson.dynamicComponents[lessonCodedConfig.id];
        if (foundConfig) {
          // Merge configurations: Start with coded, override/add with found lesson config
          lessonCodedConfig = { ...lessonCodedConfig, ...foundConfig };
        }
      }

      const LessonClass = this.dynamicComponentsService.getDynamicComponentClass(lessonCodedConfig.component);

      if (!LessonClass) {
        console.error(`Component class not found for type: ${lessonCodedConfig.component}. JSON: ${json}`);
        return null; // Return null if class doesn't exist
      }

      // Create component instance
      const componentRef = this.viewContainerRef.createComponent<LessonComponentInterface>(LessonClass);

      if (lessonCodedConfig.inputs) {
        for (const key in lessonCodedConfig.inputs) {
          if (lessonCodedConfig.inputs.hasOwnProperty(key)) {
            componentRef.instance[key] = lessonCodedConfig.inputs[key];
          }
        }
      }
      // i think i can improve this to pass only settings not all config, Assign the configuration to the component instance
      // settings data i defined in form interface, config will be all the component data including id and component type
      if (lessonCodedConfig.settings) {
        componentRef.instance.config = lessonCodedConfig;
      }
      return componentRef;
    } catch (error) {
      console.error(`Error processing component JSON: ${json}`, error);
      return null; // Return null on JSON parsing or other errors
    }
  }

  // --- Evaluation Logic ---

  public async evaluateForms() {
    this.mainForm.markAllAsTouched(); // Mark all controls for validation feedback

    if (!this.mainForm.valid) {
      // Trigger validation feedback on individual components visually if needed
      Object.keys(this.mainForm.controls).forEach((controlName) => {
        if (this.components[controlName]?.instance?.validate) {
          this.components[controlName].instance.validate(); // Assuming validate method handles visual feedback
        }
      });
      this.toastrService.warn({ subtitle: 'Por favor completa todos los ejercicios', title: 'Incompleto' });
      return;
    }

    const rates = { correct: 0, incorrect: 0, score: 0 };

    // Evaluate each component associated with a form control
    Object.keys(this.mainForm.controls).forEach((controlName) => {
      const instance = this.components[controlName]?.instance;
      // Check if the instance has an evaluate method (duck typing is okay here)
      if (instance && typeof instance.evaluate === 'function') {
        try {
          const result = instance.evaluate();
          if (result) {
            rates.correct++;
          } else {
            rates.incorrect++;
          }
        } catch (err) {
          console.error('Error during evaluation for component:', controlName, instance, err);
          rates.incorrect++; // Count errors as incorrect
        }
      }
    });

    const totalQuestions = rates.correct + rates.incorrect;
    rates.score = totalQuestions > 0 ? rates.correct / totalQuestions : 0; // Avoid division by zero

    const status = rates.score >= 0.7 ? 'passed' : 'failed'; // Use >= for threshold

    const currentLesson = this.lesson();
    if (!currentLesson) {
      console.error('Cannot save result, lesson data is missing.');
      this.toastrService.error({ subtitle: 'Cannot save result, lesson data missing.', title: 'Error' });
      return;
    }
    const takenLesson: ILessonTaken = { id: currentLesson.id, goalCompleted: null, score: rates.score, status: status, lastAccess: new Date() };

    console.log('Lesson evaluation result:', takenLesson);
    // TODO: Re-implement saving the taken lesson status via lessonService
    // try {
    //   await this.lessonService.saveTakenLesson(takenLesson);
    //   if (status === 'passed') {
    //     this.toastrService.success({ subtitle: `Calificación: ${Math.round(rates.score * 100)}%. Se guardó tu progreso.`, title: '¡Muy bien!' });
    //   } else {
    //     this.toastrService.warn({ subtitle: `Calificación: ${Math.round(rates.score * 100)}%. Revisa tus respuestas.`, title: 'Casi lo logras' });
    //   }
    // } catch (error) {
    //   console.error('Failed to save taken lesson', error);
    //   this.toastrService.error({ subtitle: 'No se pudo guardar tu progreso.', title: 'Error' });
    // }
    if (status === 'passed') {
      this.toastrService.success({ subtitle: `Calificación: ${Math.round(rates.score * 100)}%.`, title: '¡Muy bien!' });
    } else {
      this.toastrService.warn({ subtitle: `Calificación: ${Math.round(rates.score * 100)}%. Revisa tus respuestas.`, title: 'Casi lo logras' });
    }
  }

  // --- AI Chat Logic ---

  public async startAI() {
    const currentLesson = this.lesson();
    if (!currentLesson) {
      console.error('Cannot start AI without a lesson.');
      this.toastrService.error({ subtitle: 'Lesson data not available.', title: 'Cannot Start Chat' });
      return;
    }

    console.log('Requesting agent cards from LessonAIService...');
    try {
      // Call the service to get the agent cards
      const conversationSettings = await this.lessonAIService.generateConversationSettingsForLesson(currentLesson, this.settings());

      if (conversationSettings) {
        this.conversationSettings.set(conversationSettings);
        this.uiStateService.chatDrawerVisible.set(true);
        console.log('Agent cards received and set.');
      } else {
        console.error('Failed to generate agent cards (service returned null).');
        this.toastrService.error({ subtitle: 'Could not prepare the AI chat session.', title: 'Error' });
      }
    } catch (error) {
      console.error('Error generating agent cards:', error);
      this.toastrService.error({ subtitle: 'An error occurred while preparing the AI chat.', title: 'Error' });
    }
  }

  public onVisibleChange(isVisible: boolean) {
    if (isVisible === false) {
      this.uiStateService.chatDrawerVisible.set(false);
    }
  }

  public async handleGoalCompleted(event: any = {}) {
    console.log('Goal completed:', event);
    const lesson = this.lesson();

    const takenLesson: ILessonTaken = { id: lesson.id, goalCompleted: true, score: 100, status: 'finished', lastAccess: new Date() };

    const res = await this.lessonsService.saveTakenLesson(takenLesson);
    if (res) {
      // this.lessonsMemStateService.updateUserState(res);
    }

    this.toastrService.success({ subtitle: '¡Has completado la lección! , pero puedes seguir conversando', title: '¡Muy bien, guardaremos tu progreso!' });
  }

  public onChatMessage(event: ChatEvent): void {
    console.log('Received chat event:', event);

    switch (event.type) {
      case ChatEventType.WordClicked: {
        const wordClickedData = event.payload as WordData;
        console.log('Word clicked event received, emitting output:', wordClickedData);
        // Removed: this.wordPopupService.showWordPopup(wordClicked); // Use the service
        this.wordClicked.emit(wordClickedData); // Emit the event instead
        break;
      }

      default:
        console.log('Unhandled chat event type:', event.type);
    }
  }
}
